import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@ttc3k/trekker";
import {
  ApolloClient,
  ApolloProvider as ApolloClientProvider,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { useAuth0 } from "@auth0/auth0-react";
import { setContext } from "@apollo/client/link/context";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useLoggedInAs, useLoginRedirectPath } from "shared/hooks";
import {
  mergeLoginRedirectDeepLinkParams,
  mergeLoginRedirectSearchParamsFromLocation,
} from "shared/hooks/useLoginRedirectPath";
import { useImpersonation } from "shared/contextProviders/impersonation";
import { emitWorkbenchEvent } from "shared/contextProviders/workbenchSession/workbenchEvents";
import { Owner } from "gql/generated";
import { readMembershipDeepLinkParams } from "shared/utils/membershipIntent";
import { LoadingPage } from "shared/components";
import { Grid } from "styled-system/jsx";
import {
  AUTH_SESSION_FORCE_LOGOUT_CODES,
  AUTH_SESSION_LOGOUT_REASON_KEY,
  type AuthSessionLogoutReason,
} from "shared/constants/authSession";
import { getAuthSessionForceLogoutReason } from "shared/utils/getAuthSessionErrorFromApollo";
import { getAccessTokenSingleFlight } from "shared/utils/authTokenSingleFlight";

type ApolloProviderWithAuth0Props = PropsWithChildren;

export const ApolloProviderWithAuth0 = ({
  children,
}: ApolloProviderWithAuth0Props) => {
  const {
    isLoading,
    isAuthenticated,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();
  const { clearLoginRedirectPath } = useLoginRedirectPath();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const clearLoginRedirectPathRef = useRef(clearLoginRedirectPath);
  clearLoginRedirectPathRef.current = clearLoginRedirectPath;
  const isAuthLogoutInProgressRef = useRef(false);
  const { t } = useTranslation();
  const [authLogoutReason, setAuthLogoutReason] =
    useState<AuthSessionLogoutReason | null>(null);

  useEffect(() => {
    if (user) {
      setAuthLogoutReason(null);
      return;
    }
    const reason = sessionStorage.getItem(
      AUTH_SESSION_LOGOUT_REASON_KEY,
    ) as AuthSessionLogoutReason | null;
    setAuthLogoutReason(
      AUTH_SESSION_FORCE_LOGOUT_CODES.includes(
        reason as AuthSessionLogoutReason,
      )
        ? (reason as AuthSessionLogoutReason)
        : null,
    );
  }, [user]);

  // Ensure the user is logged in
  useEffect(() => {
    if (!user && !isAuthenticated && !isLoading) {
      const broadcast = searchParams.get("broadcast");
      const resource = searchParams.get("resource");
      const organizationId = searchParams.get("organizationId");

      if (location.search) {
        mergeLoginRedirectSearchParamsFromLocation(location.search);
      } else if (broadcast || organizationId) {
        mergeLoginRedirectDeepLinkParams({
          broadcast,
          resource,
          organizationId,
          ...readMembershipDeepLinkParams(searchParams),
        });
      }

      if (broadcast && organizationId) {
        const redirectParams = new URLSearchParams();
        redirectParams.set("organizationId", organizationId);
        redirectParams.set("broadcast", broadcast);
        if (resource) redirectParams.set("resource", resource);
        const { intent, membership } =
          readMembershipDeepLinkParams(searchParams);
        if (intent) redirectParams.set("intent", intent);
        if (membership) redirectParams.set("membership", membership);
        navigate(
          `/?${redirectParams.toString()}`,
          { replace: true },
        );
        return;
      }
      loginWithRedirect({
        appState: {
          returnTo: `${location.pathname}${location.search}`,
        },
      });
    }
  }, [
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    location.search,
    navigate,
    searchParams,
    user,
  ]);

  // Determine who we're logged in as
  const loggedInAsRef = useRef<Owner | null>(null);
  loggedInAsRef.current = useLoggedInAs();

  // Track impersonation state for the Apollo link
  const { impersonatedUserId, impersonationKind } = useImpersonation();
  const impersonatedUserIdRef = useRef<string | null>(null);
  impersonatedUserIdRef.current = impersonatedUserId;
  const impersonationKindRef = useRef(impersonationKind);
  impersonationKindRef.current = impersonationKind;

  // Set up the Apollo context (we need to use Refs to ensure we have the current variable value)
  const apolloLink = setContext(async (_, { headers, ...rest }) => {
    // Coalesce concurrent token requests (multiple queries fire on app load)
    // and retry transient Auth0 lock-acquisition failures — see
    // authTokenSingleFlight.ts for why this matters.
    const token = await getAccessTokenSingleFlight(() =>
      getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        },
      }),
    );

    if (!token) return { headers, ...rest };

    return {
      ...rest,
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
        // A call site may explicitly set `context: { headers: { metadata } }`
        // (already JSON-stringified) to override the URL-derived value below —
        // e.g. the Home dashboard has no operator/organization segment in its
        // URL, so `loggedInAsRef.current` is null there even though the user
        // is acting as a specific operator for a specific event.
        metadata: headers?.metadata ?? JSON.stringify(loggedInAsRef.current),
        ...(impersonatedUserIdRef.current &&
          impersonationKindRef.current === "test" && {
            "x-test-user": impersonatedUserIdRef.current,
          }),
        ...(impersonatedUserIdRef.current &&
          impersonationKindRef.current !== "test" && {
            "x-impersonate-user": impersonatedUserIdRef.current,
          }),
      },
    };
  });

  // Setup the Apollo client
  const httpLink = new HttpLink({
    uri: import.meta.env.VITE_API_SERVER_URL + "/graphql",
  });
  const removeTypenameLink = removeTypenameFromVariables();
  const authSessionErrorLink = onError(({ graphQLErrors, networkError, forward, operation }) => {
    if (!operation.operationName?.startsWith("Workbench")) {
      for (const error of graphQLErrors ?? []) {
        emitWorkbenchEvent({
          type: "gqlError",
          operationName: operation.operationName,
          message: error.message,
        });
      }
      if (networkError) {
        emitWorkbenchEvent({
          type: "gqlError",
          operationName: operation.operationName,
          message: `Network error: ${networkError.message}`,
        });
      }
    }
    if (isAuthLogoutInProgressRef.current) return forward(operation);

    const authErrorCode = getAuthSessionForceLogoutReason(
      graphQLErrors,
      networkError,
    );
    if (!authErrorCode) return forward(operation);

    isAuthLogoutInProgressRef.current = true;
    sessionStorage.setItem(AUTH_SESSION_LOGOUT_REASON_KEY, authErrorCode);
    clearLoginRedirectPathRef.current();
    logoutRef.current({
      logoutParams: { returnTo: `${window.location.origin}/app` },
    });
    return forward(operation);
  });
  const link = from([
    authSessionErrorLink,
    apolloLink,
    removeTypenameLink,
    httpLink,
  ]);
  const apolloClient = useRef<ApolloClient<NormalizedCacheObject> | null>(null);

  if (!apolloClient.current) {
    apolloClient.current = new ApolloClient({
      link,
      cache: new InMemoryCache(),
      defaultOptions: {
        query: {
          fetchPolicy: "no-cache",
        },
        mutate: {
          fetchPolicy: "no-cache",
        },
        watchQuery: {
          fetchPolicy: "no-cache",
        },
      },
    });
  }

  if (!user) {
    return (
      <Grid
        height="svh"
        width="full"
        backgroundColor="bg.wa.lightest"
        flexDir="column"
        gap="200"
        alignItems="center"
        justifyContent="center"
        px="400"
      >
        {authLogoutReason && (
          <Text
            visual="bodyMedium"
            color="text.mid"
            textAlign="center"
            maxW="32rem"
          >
            {t(`core:ERROR.AUTH_SESSION.${authLogoutReason}`)}
          </Text>
        )}
        <LoadingPage />
      </Grid>
    );
  }

  return (
    <ApolloClientProvider client={apolloClient.current}>
      {children}
    </ApolloClientProvider>
  );
};
